import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import TimelineYogaHistory from './TimelineYogaHistory';
import YogaStylesFlipGrid from './YogaStylesFlipGrid';
import BreathMeditationBasics from './BreathMeditationBasics';
import YogaArchetypeQuiz from './YogaArchetypeQuiz';
import './YogaLore.css';

const YogaLore = () => {
  const { t, i18n } = useTranslation();
  
  // Language detection 
  const currentLanguage = i18n.language;
  const isSlovak = currentLanguage === 'sk';

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Add SEO structured data
  useEffect(() => {
    const addStructuredData = () => {
      // Create educational content schema
      const yogaLoreData = {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": isSlovak ? "Podstata jogy" : "The Essence of Yoga",
        "description": isSlovak ? 
          "Joga, zo sanskritského slova Yuj znamenajúceho \"spojenie,\" je integrácia tela, dychu a mysle. Presahuje fyzické pozície, pomáhajúc nám dosiahnuť harmóniu v nás samých." : 
          "Yoga, from the Sanskrit word Yuj meaning \"union,\" is the integration of body, breath, and mind. It goes beyond physical postures, helping us achieve harmony within ourselves.",
        "provider": {
          "@type": "Organization",
          "name": "TiDeaSolis Yoga Štúdio",
          "sameAs": "https://www.tideasolisyoga.sk/"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Yoga practitioners and enthusiasts"
        },
        "educationalLevel": "Beginner",
        "teaches": [
          "Yoga history",
          "Meditation techniques",
          "Breathing techniques",
          "Yoga styles"
        ],
        "hasCourseInstance": {
          "@type": "CourseInstance",
          "courseMode": "online"
        }
      };
      
      // Add or update JSON-LD script
      let scriptTag = document.getElementById('yoga-lore-schema');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = 'yoga-lore-schema';
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(yogaLoreData);
      
      // Set page title if Slovak
      if (isSlovak) {
        document.title = "Podstata jogy | TiDeaSolis Yoga Štúdio";
        
        // Update or create meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.setAttribute('name', 'description');
          document.head.appendChild(metaDescription);
        }
        metaDescription.setAttribute('content', "Objavte podstatu jogy, jej históriu, základné dychové techniky a rôzne štýly jogy. Zistite, ktorý štýl je pre vás najvhodnejší prostredníctvom nášho kvízu.");
      }
    };
    
    addStructuredData();
    
    return () => {
      // Optional cleanup
      const script = document.getElementById('yoga-lore-schema');
      if (script) {
        script.remove();
      }
    };
  }, [isSlovak]);

  return (
    <section 
      className="yoga-lore-section" 
      lang={currentLanguage}
      itemScope 
      itemType="https://schema.org/EducationalOccupationalCredential"
    >
      <meta itemProp="name" content={isSlovak ? "Podstata jogy" : "The Essence of Yoga"} />
      <meta itemProp="educationalLevel" content="Beginner" />
      
      <div className="yoga-lore-container">
        <h2 
          className="yoga-lore-title" 
          itemProp="credentialCategory"
          lang={currentLanguage}
        >
          {t("yoga_lore_title", "The Essence of Yoga")}
        </h2>
        
        <div className="yoga-lore-content" itemProp="description">
          <p 
            className="yoga-lore-paragraph"
            lang={currentLanguage}
            dangerouslySetInnerHTML={{ 
              __html: t("yoga_lore_paragraph", "Yoga, from the Sanskrit word <em>Yuj</em> meaning \"union,\" is the integration of body, breath, and mind. It goes beyond physical postures, helping us achieve harmony within ourselves. Mindful movement creates inner space, conscious breathing generates vital energy, and meditation calms the mind. Together, these practices guide us toward self-awareness, presence, and connection to something deeper. Yoga is a personal yet universal journey—merging outer form with inner transformation. In today's busy world, yoga provides a sanctuary to reconnect, restoring balance, awareness, and joy.") 
            }}
          />
          
          <TimelineYogaHistory />
          
          <BreathMeditationBasics />
          
          <YogaStylesFlipGrid />
          
          <div className="yoga-archetype-quiz-section" itemScope itemType="https://schema.org/Quiz">
            <meta itemProp="name" content={isSlovak ? "Objavte svoj jogový archetyp" : "Discover Your Yoga Archetype"} />
            
            <h3 
              className="yoga-quiz-heading" 
              itemProp="about"
              lang={currentLanguage}
            >
              {t("yoga_archetype_quiz.heading", "Discover Your Yoga Archetype")}
            </h3>
            <p 
              className="yoga-quiz-intro"
              lang={currentLanguage}
              itemProp="description"
            >
              {t("yoga_archetype_quiz.intro", "Curious about which yoga style might suit you best? Take this quiz to discover your yoga archetype and receive personalized style recommendations based on your temperament and preferences.")}
            </p>
            <YogaArchetypeQuiz />
          </div>
        </div>
      </div>
    </section>
  );
};

export default YogaLore; 